import { QuestionLong } from '@repo/lib/quiz/questions/class/QuestionLong';
import { PencilLineIcon } from 'lucide-react';
import { useMemo } from 'react';

import {
  QuestionMarkedDescription,
  QuestionMarkedHeader,
  QuestionMarkedNoAnswer,
  QuestionMarkedQuestionContent,
  QuestionMarkedUserAnswer,
  QuestionMarkedWrapper,
} from '@/components/features/quiz/question/question-marked';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { useQuestionMarkedForm } from '@/hooks/questions';

import type { QuestionMarkedLongProps } from '.';

import { QuestionMarkedLongForm } from '.';

export function QuestionMarkedLong(questionMarkedLongProps: QuestionMarkedLongProps) {
  const { marked, question, showMarked, showQuestion, answer, onEditMark } = questionMarkedLongProps;
  const { formRef, wrapperRef, openForm, setOpenForm, openFormHandler, closeFormHandler } = useQuestionMarkedForm();
  const questionObj = useMemo<QuestionLong>(() => {
    const qClass = new QuestionLong(question, null, marked);
    if (answer) qClass.importAnswer(answer);
    return qClass;
  }, [question, marked, answer]);

  const correctState = questionObj.correctState();
  const score: number = questionObj.getPoints();
  const totalPoints: number = questionObj.getMaxPoints();
  const correct: boolean = questionObj.getMarked()?.correct ?? false;

  if (!showMarked) return null;

  /*
    Only a missing answer means "unanswered" — an answer still waiting for its
    manual grade has no feedback attached yet, and must not read as one.
  */
  if (!questionObj.isAnswered()) {
    return <QuestionMarkedNoAnswer showMarked={showMarked} />;
  }
  return (
    <QuestionMarkedWrapper showMarked={showMarked} ref={wrapperRef}>
      <QuestionMarkedHeader
        score={score}
        totalPoints={totalPoints}
        correct={correct}
        manualCheck={marked?.manualCheck ?? true}
        correctType={correctState}
        headerActions={
          onEditMark && (
            <Button
              variantColor={correctState === 'waiting' ? 'destructive' : 'default'}
              variant={correctState === 'waiting' ? 'secondary' : 'tertiary'}
              size="sm"
              disabled={openForm}
              onClick={openFormHandler}
            >
              <PencilLineIcon />
              Oceń
            </Button>
          )
        }
      />
      <QuestionMarkedQuestionContent showQuestion={showQuestion} content={question.content} />
      <QuestionMarkedUserAnswer>
        <div className="space-y-2">
          <p className="text-accent-foreground text-sm font-medium">Odpowiedziano:</p>
          <p className="text-accent-foreground">{marked?.answer || questionObj.getAnswer()?.answer}</p>
        </div>

        <div className="space-y-2">
          <p className="text-accent-foreground text-sm font-medium">Wyjaśnienie:</p>
          <p className="text-muted-foreground text-sm">{marked?.explanation ? marked.explanation : 'Brak wyjaśnienia'}</p>
        </div>
      </QuestionMarkedUserAnswer>
      <Collapsible open={openForm} onOpenChange={setOpenForm} className="-my-px">
        <CollapsibleContent ref={formRef}>
          <QuestionMarkedDescription />
          <QuestionMarkedLongForm onSubmit={onEditMark} onClose={closeFormHandler} marked={marked} isOpen={openForm} maxPoints={totalPoints} />
        </CollapsibleContent>
      </Collapsible>
    </QuestionMarkedWrapper>
  );
}
